// @givmo/design-system — Card (SaaS layer).
//
// A surface container for the rebuilt admin/super-admin portals: white `surface`, a 1px
// `border-subtle` hairline, `rounded-xl` (12px) corners, and a soft `shadow-xs` elevation —
// the Stripe/Mercury panel. Composes with `CardHeader` / `CardBody` / `CardFooter` for a
// titled section with a divider-bounded header and footer.
import { forwardRef } from 'react';
import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from '../lib/utils';
import { testIdProp, type TestIdProps } from '../lib/testid';

export interface CardProps extends HTMLAttributes<HTMLDivElement>, TestIdProps {
  /** Drop the default `shadow-xs` for a flat, border-only card. */
  flush?: boolean;
}

export const Card = forwardRef<HTMLDivElement, CardProps>(function Card(
  { className, flush = false, testId, children, ...props },
  ref,
) {
  return (
    <div
      ref={ref}
      className={cn(
        'rounded-xl border border-border-subtle bg-surface',
        flush ? '' : 'shadow-xs',
        className,
      )}
      {...testIdProp(testId)}
      {...props}
    >
      {children}
    </div>
  );
});

export interface CardHeaderProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'title'>, TestIdProps {
  /** Section title (Lato w600 s16, text-strong). */
  title?: ReactNode;
  /** Optional one-line subtitle (text-tertiary). */
  subtitle?: ReactNode;
  /** Trailing actions slot (buttons, menu, filter). */
  actions?: ReactNode;
}

export const CardHeader = forwardRef<HTMLDivElement, CardHeaderProps>(function CardHeader(
  { className, title, subtitle, actions, testId, children, ...props },
  ref,
) {
  return (
    <div
      ref={ref}
      className={cn(
        'flex items-start justify-between gap-16 border-b border-border-subtle px-20 py-16',
        className,
      )}
      {...testIdProp(testId)}
      {...props}
    >
      {children ?? (
        <div className="min-w-0">
          {title != null && (
            <h3 className="truncate font-sans text-s16 font-w600 tracking-givmo text-text-strong">
              {title}
            </h3>
          )}
          {subtitle != null && (
            <p className="mt-2 font-sans text-s13 font-w400 tracking-givmo text-text-tertiary">
              {subtitle}
            </p>
          )}
        </div>
      )}
      {actions != null && <div className="flex shrink-0 items-center gap-8">{actions}</div>}
    </div>
  );
});

export interface CardBodyProps extends HTMLAttributes<HTMLDivElement>, TestIdProps {
  /** Remove the default padding (for edge-to-edge tables). */
  flush?: boolean;
}

export const CardBody = forwardRef<HTMLDivElement, CardBodyProps>(function CardBody(
  { className, flush = false, testId, children, ...props },
  ref,
) {
  return (
    <div
      ref={ref}
      className={cn(flush ? '' : 'p-20', className)}
      {...testIdProp(testId)}
      {...props}
    >
      {children}
    </div>
  );
});

export interface CardFooterProps extends HTMLAttributes<HTMLDivElement>, TestIdProps {}

export const CardFooter = forwardRef<HTMLDivElement, CardFooterProps>(function CardFooter(
  { className, testId, children, ...props },
  ref,
) {
  return (
    <div
      ref={ref}
      className={cn(
        'flex items-center justify-between gap-16 border-t border-border-subtle px-20 py-14',
        className,
      )}
      {...testIdProp(testId)}
      {...props}
    >
      {children}
    </div>
  );
});
